import { apiGet, apiPost, apiPatch, apiDelete } from './api';
import type { HalCollection } from '@/types';

// Tipuri pentru Status
export interface StatusData {
  id: number;
  name: string;
  color: string;
  isClosed: boolean;
  isDefault: boolean;
  position: number;
}

export interface StatusFormData {
  name: string;
  color?: string;
  isClosed?: boolean;
  isDefault?: boolean;
  position?: number;
}

// Tipuri pentru Priority
export interface PriorityData {
  id: number;
  name: string;
  color: string;
  isDefault: boolean;
  isActive: boolean;
  position: number;
}

export interface PriorityFormData {
  name: string;
  color?: string;
  isDefault?: boolean;
  isActive?: boolean;
  position?: number;
}

// ============== STATUSURI ==============

// Obține lista statusurilor (admin)
export async function getAdminStatuses(): Promise<StatusData[]> {
  const response = await apiGet<HalCollection<StatusData>>('/auth/data/admin/statuses');
  return response._embedded?.elements || [];
}

// Crează un status nou
export async function createStatus(data: StatusFormData): Promise<StatusData> {
  return apiPost<StatusData>('/auth/data/admin/statuses', data);
}

// Actualizează un status existent
export async function updateStatus(id: number, data: Partial<StatusFormData>): Promise<StatusData> {
  return apiPatch<StatusData>(`/auth/data/admin/statuses/${id}`, data);
}

// Șterge un status
export async function deleteStatus(id: number): Promise<void> {
  return apiDelete(`/auth/data/admin/statuses/${id}`);
}

// ============== PRIORITĂȚI ==============

// Obține lista priorităților (admin)
export async function getAdminPriorities(): Promise<PriorityData[]> {
  const response = await apiGet<HalCollection<PriorityData>>('/auth/data/admin/priorities');
  return response._embedded?.elements || [];
}

// Crează o prioritate nouă
export async function createPriority(data: PriorityFormData): Promise<PriorityData> {
  return apiPost<PriorityData>('/auth/data/admin/priorities', data);
}

// Actualizează o prioritate existentă
export async function updatePriority(id: number, data: Partial<PriorityFormData>): Promise<PriorityData> {
  return apiPatch<PriorityData>(`/auth/data/admin/priorities/${id}`, data);
}

// ============== TIPURI DOCUMENTE ACHIZIȚII ==============

export interface ProcurementDocType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getProcurementDocTypes(): Promise<ProcurementDocType[]> {
  return apiGet<ProcurementDocType[]>('/auth/data/nomenclatoare/procurement-doc-types');
}

export async function createProcurementDocType(data: { code?: string; name: string; sort_order?: number }): Promise<ProcurementDocType> {
  return apiPost<ProcurementDocType>('/auth/data/nomenclatoare/procurement-doc-types', data);
}

export async function updateProcurementDocType(id: number, data: Partial<{ code: string; name: string; sort_order: number; is_active: boolean }>): Promise<ProcurementDocType> {
  return apiPatch<ProcurementDocType>(`/auth/data/nomenclatoare/procurement-doc-types/${id}`, data);
}

export async function deleteProcurementDocType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/procurement-doc-types/${id}`);
}

// ============== TIPURI DOCUMENTE LIVRABILE ==============

export interface DeliverableDocType {
  id: number;
  code: string;
  name: string;
  deliverable_type: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getDeliverableDocTypes(): Promise<DeliverableDocType[]> {
  return apiGet<DeliverableDocType[]>('/auth/data/nomenclatoare/deliverable-doc-types');
}

export async function createDeliverableDocType(data: { code?: string; name: string; deliverable_type?: string; sort_order?: number }): Promise<DeliverableDocType> {
  return apiPost<DeliverableDocType>('/auth/data/nomenclatoare/deliverable-doc-types', data);
}

export async function updateDeliverableDocType(id: number, data: Partial<{ code: string; name: string; deliverable_type: string; sort_order: number; is_active: boolean }>): Promise<DeliverableDocType> {
  return apiPatch<DeliverableDocType>(`/auth/data/nomenclatoare/deliverable-doc-types/${id}`, data);
}

export async function deleteDeliverableDocType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/deliverable-doc-types/${id}`);
}

// ============== TEMPLATE SUGESTII LIVRABILE ==============

export interface DeliverableTemplateSuggestion {
  code: string;
  name: string;
  parentCode: string | null;
}

export async function getDeliverableTemplateSuggestions(type: string): Promise<DeliverableTemplateSuggestion[]> {
  return apiGet<DeliverableTemplateSuggestion[]>(`/auth/data/nomenclatoare/deliverable-templates/${type}`);
}

// ============== TIPURI LIVRABILE (SF, PT, DALI, DTAC) ==============

export interface DeliverableType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getDeliverableTypes(): Promise<DeliverableType[]> {
  return apiGet<DeliverableType[]>('/auth/data/nomenclatoare/deliverable-types');
}

export async function createDeliverableType(data: { code: string; name: string; sort_order?: number }): Promise<DeliverableType> {
  return apiPost<DeliverableType>('/auth/data/nomenclatoare/deliverable-types', data);
}

export async function updateDeliverableType(id: number, data: Partial<{ code: string; name: string; sort_order: number; is_active: boolean }>): Promise<DeliverableType> {
  return apiPatch<DeliverableType>(`/auth/data/nomenclatoare/deliverable-types/${id}`, data);
}

export async function deleteDeliverableType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/deliverable-types/${id}`);
}

// ============== TIPURI IMPLEMENTARE (EXECUTIE LUCRARI, PROIECTARE/EXECUTIE) ==============

export interface ImplementationType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getImplementationTypes(): Promise<ImplementationType[]> {
  return apiGet<ImplementationType[]>('/auth/data/nomenclatoare/implementation-types');
}

export async function createImplementationType(data: { code: string; name: string; sort_order?: number }): Promise<ImplementationType> {
  return apiPost<ImplementationType>('/auth/data/nomenclatoare/implementation-types', data);
}

export async function updateImplementationType(id: number, data: Partial<{ code: string; name: string; sort_order: number; is_active: boolean }>): Promise<ImplementationType> {
  return apiPatch<ImplementationType>(`/auth/data/nomenclatoare/implementation-types/${id}`, data);
}

export async function deleteImplementationType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/implementation-types/${id}`);
}

// ============== TIPURI DOCUMENTE IMPLEMENTARE ==============

export interface ImplementationDocType {
  id: number;
  code: string;
  name: string;
  implementation_type: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getImplementationDocTypes(): Promise<ImplementationDocType[]> {
  return apiGet<ImplementationDocType[]>('/auth/data/nomenclatoare/implementation-doc-types');
}

export async function createImplementationDocType(data: { code?: string; name: string; implementation_type?: string; sort_order?: number }): Promise<ImplementationDocType> {
  return apiPost<ImplementationDocType>('/auth/data/nomenclatoare/implementation-doc-types', data);
}

export async function updateImplementationDocType(id: number, data: Partial<{ code: string; name: string; implementation_type: string; sort_order: number; is_active: boolean }>): Promise<ImplementationDocType> {
  return apiPatch<ImplementationDocType>(`/auth/data/nomenclatoare/implementation-doc-types/${id}`, data);
}

export async function deleteImplementationDocType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/implementation-doc-types/${id}`);
}

// ============== TIPURI CONTRACTE ==============

export interface ContractType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export async function getContractTypes(): Promise<ContractType[]> {
  return apiGet<ContractType[]>('/auth/data/nomenclatoare/contract-types');
}

export async function createContractType(data: { code: string; name: string; sort_order?: number }): Promise<ContractType> {
  return apiPost<ContractType>('/auth/data/nomenclatoare/contract-types', data);
}

export async function updateContractType(id: number, data: Partial<{ code: string; name: string; sort_order: number; is_active: boolean }>): Promise<ContractType> {
  return apiPatch<ContractType>(`/auth/data/nomenclatoare/contract-types/${id}`, data);
}

export async function deleteContractType(id: number): Promise<void> {
  return apiDelete(`/auth/data/nomenclatoare/contract-types/${id}`);
}
